import Box from '@mui/material/Box';
import DialogContentText from '@mui/material/DialogContentText';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import { useMemo, useState, useEffect } from 'react';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
    FieldError,
    FieldValues,
    useController,
    useFieldArray,
} from 'react-hook-form';

import {
    IContentTypeValue,
    IExtraCategoryOption,
    IGroupListItemFragment,
    GroupListItemFragmentDoc,
    ExtraCategoriesDocument,
    ExtraCategoryFiltersDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { Loader } from 'common/components/Loader';
import { getExtraCategoryFilterItems } from 'organisation/utils/extraCategory';
import { getSelectedGroupConditionFilters } from 'user/utils/groups';
import { AddButton } from 'common/components/Button/AddButton';
import { IGroupConditionInput, TGroupListItem } from 'user/types';
import { TControllerProps, TFilterBarItem } from 'common/types';
import { GroupSelectDrawer } from 'user/components/GroupSelectDrawer';
import { AlertDialog } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import { GroupConditionListItem } from 'user/forms/Field/GroupConditionFilterField/GroupConditionListItem';
import { asTypedControl } from 'common/utils/form';

interface IProps {
    name: string;
}

export const GroupConditionFilterField = <TFieldValues extends FieldValues>({
    control: formControl,
    name,
}: IProps & TControllerProps<TFieldValues>) => {
    const { showApolloError } = useApolloError();
    const [translate] = useTranslation();
    const client = useApolloClient();
    const {
        data: extraCategoriesData,
        loading: extraCategoriesLoading,
        error: extraCategoriesError,
    } = useQuery(ExtraCategoriesDocument, {
        variables: { contentType: IContentTypeValue.Group },
    });

    useEffect(() => {
        if (extraCategoriesError) {
            showApolloError(extraCategoriesError);
        }
    }, [extraCategoriesError, showApolloError]);

    const {
        data: extraCategoryFiltersData,
        loading: extraCategoryFiltersLoading,
        error: extraCategoryFiltersError,
    } = useQuery(ExtraCategoryFiltersDocument, {
        variables: { contentType: IContentTypeValue.Group },
    });

    useEffect(() => {
        if (extraCategoryFiltersError) {
            showApolloError(extraCategoryFiltersError);
        }
    }, [extraCategoryFiltersError, showApolloError]);
    const [selectedFilterIndex, setSelectedFilterIndex] = useState<
        number | undefined
    >();
    const [selectDrawerOpen, setSelectDrawerOpen] = useState(false);
    const [showOnDeleteAlert, setShowOnDeleteAlert] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState<TGroupListItem[]>([]);

    const control = asTypedControl<IGroupConditionInput[]>(name, formControl);

    const {
        fieldState: { error: fieldError },
    } = useController({
        name,
        control,
    });

    const errorMessage = (fieldError?.[name as keyof FieldError] as FieldError)
        ?.message;

    const {
        fields: groupConditionInputs,
        append,
        remove,
        update,
    } = useFieldArray({
        control,
        name,
        keyName: 'key',
    });

    const { extraCategories } = extraCategoriesData || {};
    const { extraCategoriesFilters } = extraCategoryFiltersData || {};

    // Only use choice type filters
    const filteredCategoryFilters = extraCategoriesFilters?.filter(
        (categoryFilter) =>
            categoryFilter.categoryType === IExtraCategoryOption.Choice
    );

    const loading = extraCategoriesLoading || extraCategoryFiltersLoading;

    // Retrieve group objects from cache based on UIDs stored in the form
    const getGroupsFromCache = useMemo(
        () =>
            (groupIds: string[]): TGroupListItem[] =>
                (groupIds
                    ?.map((groupId) => {
                        try {
                            const group =
                                client.cache.readFragment<IGroupListItemFragment>(
                                    {
                                        id: `Group:${groupId}`,
                                        fragment: GroupListItemFragmentDoc,
                                        fragmentName: 'GroupListItem',
                                    }
                                );

                            if (!group) return null;

                            // Remove isEditable to match TGroupListItem type
                            const { isEditable, ...groupListItem } = group;

                            return groupListItem as TGroupListItem;
                        } catch {
                            return null;
                        }
                    })
                    .filter(Boolean) || []) as TGroupListItem[],
        [client.cache]
    );

    const handleSelectGroup = (group: TGroupListItem) => {
        const newGroups = [...selectedGroups, group].sort((a, b) =>
            a.name > b.name ? 1 : -1
        );

        setSelectedGroups(newGroups);
    };

    const handleAddGroups = (
        selectedGroups: TGroupListItem[],
        selectedFilters?: TFilterBarItem[]
    ) => {
        // Extract UIDs from selected groups
        const groupIds = selectedGroups.map((group) => group.id);

        // If existing group condition is being edited, replace it with new values
        if (selectedFilterIndex !== undefined && groupConditionInputs?.length) {
            const newGroupConditionInput = {
                ...groupConditionInputs[selectedFilterIndex],
                extraCategoryValues:
                    selectedFilters
                        ?.map(({ extraCategoryData, value }) => {
                            if (!extraCategoryData?.id) return null;

                            const extraCategory = extraCategories?.find(
                                (extraCategory) =>
                                    extraCategory.id === extraCategoryData.id
                            );
                            const color = extraCategory?.choiceOptions?.find(
                                (option) => option.stringValue === value
                            )?.color;

                            return {
                                id: extraCategoryData.id,
                                stringValue: value,
                                color,
                            };
                        })
                        .filter(Boolean) || [],
                groups: groupIds,
            };

            update(selectedFilterIndex, newGroupConditionInput);
        } else {
            const newGroupConditionInput = {
                extraCategoryValues:
                    selectedFilters
                        ?.map(({ extraCategoryData, value }) => {
                            if (!extraCategoryData?.id) return null;

                            const extraCategory = extraCategories?.find(
                                (extraCategory) =>
                                    extraCategory.id === extraCategoryData.id
                            );
                            const color = extraCategory?.choiceOptions?.find(
                                (option) => option.stringValue === value
                            )?.color;

                            return {
                                id: extraCategoryData.id,
                                stringValue: value,
                                color,
                            };
                        })
                        .filter(Boolean) || [],
                groups: groupIds,
            };

            append(newGroupConditionInput);
        }

        setSelectedFilterIndex(undefined);
        setSelectDrawerOpen(false);
    };

    const addGroupCondition = () => {
        // Clear selected groups when adding a new group condition
        setSelectedGroups([]);

        setSelectDrawerOpen(true);
    };

    const addedGroups = useMemo(() => {
        if (
            selectedFilterIndex === undefined ||
            !groupConditionInputs?.length
        ) {
            return [];
        }

        const groupIds = groupConditionInputs[selectedFilterIndex].groups;

        return getGroupsFromCache(groupIds);
    }, [selectedFilterIndex, groupConditionInputs, getGroupsFromCache]);

    const selectedFilters = useMemo(() => {
        if (
            selectedFilterIndex === undefined ||
            !groupConditionInputs?.length
        ) {
            return [];
        }

        const { extraCategoryValues } =
            groupConditionInputs[selectedFilterIndex];

        return getSelectedGroupConditionFilters(
            extraCategoryValues,
            getExtraCategoryFilterItems(filteredCategoryFilters)
        );
    }, [selectedFilterIndex, groupConditionInputs, extraCategoriesFilters]);

    const drawerOpen = selectDrawerOpen || selectedFilterIndex !== undefined;

    return (
        <Box>
            {loading && (
                <Box sx={{ height: 40, position: 'relative', mb: 2 }}>
                    <Loader />
                </Box>
            )}

            {!loading && (
                <>
                    <AddButton
                        aria-label={translate('groupConditionFilter.addButton')}
                        onClick={addGroupCondition}
                    >
                        {translate('groupConditionFilter.addButton')}
                    </AddButton>

                    {!!errorMessage && (
                        <FormHelperText error sx={{ mt: 2 }}>
                            {errorMessage}
                        </FormHelperText>
                    )}

                    {!!groupConditionInputs?.length && (
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            {groupConditionInputs.map(
                                (groupConditionInput, index) => {
                                    const {
                                        extraCategoryValues,
                                        groups: groupIds,
                                    } = groupConditionInput;

                                    // Retrieve group objects from cache for this group condition
                                    const groups = getGroupsFromCache(groupIds);

                                    // If there is a group that the user does not have permission to update (is group manager of),
                                    // the user cannot delete the group condition
                                    const canDelete = !groups.some(
                                        ({ permissions }) => {
                                            const groupPermissions = permissions
                                                ? JSON.parse(permissions)
                                                : undefined;

                                            return (
                                                !groupPermissions ||
                                                !groupPermissions.canUpdate
                                            );
                                        }
                                    );

                                    return (
                                        <GroupConditionListItem
                                            extraCategoryValues={
                                                extraCategoryValues
                                            }
                                            groups={groups}
                                            key={index}
                                            onClickDelete={() => {
                                                if (canDelete) {
                                                    remove(index);

                                                    return;
                                                }

                                                setShowOnDeleteAlert(true);
                                            }}
                                            onClickEdit={() => {
                                                const groupIds =
                                                    groupConditionInputs[index]
                                                        .groups;

                                                // Retrieve group objects from cache and set them for editing
                                                if (groupIds) {
                                                    const retrievedGroups =
                                                        getGroupsFromCache(
                                                            groupIds
                                                        );

                                                    setSelectedGroups(
                                                        retrievedGroups
                                                    );
                                                }

                                                setSelectedFilterIndex(index);
                                            }}
                                        />
                                    );
                                }
                            )}
                        </Stack>
                    )}

                    <AlertDialog
                        actions={
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setShowOnDeleteAlert(false);
                                }}
                            >
                                {translate('close')}
                            </Button>
                        }
                        open={showOnDeleteAlert}
                        title={translate(
                            'offerEventForm.cantDeleteGroupCondition.title'
                        )}
                    >
                        <DialogContentText color="text.primary" variant="body2">
                            {translate(
                                'offerEventForm.cantDeleteGroupCondition.text'
                            )}
                        </DialogContentText>
                    </AlertDialog>

                    <GroupSelectDrawer
                        addedGroups={addedGroups}
                        initialSelectedFilters={selectedFilters}
                        open={drawerOpen}
                        selectedGroups={selectedGroups}
                        setSelectedGroups={setSelectedGroups}
                        onAddGroups={(groups, selectedFilters) =>
                            handleAddGroups(groups || [], selectedFilters)
                        }
                        onClose={() => {
                            setSelectedFilterIndex(undefined);
                            setSelectDrawerOpen(false);
                        }}
                        onRemoveGroup={(group) => {
                            setSelectedGroups(
                                selectedGroups.filter(
                                    (selectedGroup) =>
                                        selectedGroup.id !== group.id
                                )
                            );
                        }}
                        onSelectGroup={handleSelectGroup}
                    />
                </>
            )}
        </Box>
    );
};
